import { create } from 'zustand';
import { buildKeycloakLogoutUrl } from '@/lib/keycloak';
import { setStoredActiveTenantId, withResolvedTenantContext } from '@/lib/tenant-context';
import type { User } from '@/types';

const ALL_AUTH_STORAGE_KEYS = [
  'accessToken',
  'refreshToken',
  'token',
  'idToken',
  'expiresAt',
  'user',
] as const;

const LEGACY_TOKEN_STORAGE_KEYS = ['accessToken', 'refreshToken', 'token', 'idToken'] as const;

function getApiBaseUrl() {
  return (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
}

function clearClientAuthStorage() {
  for (const key of ALL_AUTH_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
}

function clearLegacyTokenStorage() {
  for (const key of LEGACY_TOKEN_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
}

async function notifyBackendLogout() {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 2000);

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/v1/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
      },
      body: '{}',
    });

    if (!response.ok) {
      return null;
    }

    const payload = (await response.json()) as
      { data?: { logoutUrl?: string | null } } | { logoutUrl?: string | null };

    if ('data' in payload && payload.data) {
      return payload.data?.logoutUrl || null;
    }

    return (payload as { logoutUrl?: string | null }).logoutUrl || null;
  } catch {
    // Best-effort cleanup only. Frontend logout should still continue.
    return null;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function normalizeUser(user: User): User {
  const trimmedFirstName = user.firstName?.trim() ?? '';
  const trimmedLastName = user.lastName?.trim() ?? '';

  if (trimmedFirstName && trimmedLastName) {
    return withResolvedTenantContext({
      ...user,
      firstName: trimmedFirstName,
      lastName: trimmedLastName,
    });
  }

  const fallbackName =
    user.displayName?.trim() || user.email?.split('@')[0]?.replace(/[._-]+/g, ' ') || '';
  const [fallbackFirstName = 'Loans', ...fallbackLastNameParts] = fallbackName
    .split(' ')
    .map(part => part.trim())
    .filter(Boolean);
  const fallbackLastName = fallbackLastNameParts.join(' ') || 'User';

  return withResolvedTenantContext({
    ...user,
    firstName: trimmedFirstName || fallbackFirstName,
    lastName: trimmedLastName || fallbackLastName,
  });
}

interface AuthState {
  user: User | null;
  expiresAt: number | null; // timestamp (ms) when the browser session should be refreshed
  isAuthenticated: boolean;

  login: (user: User, expiresIn: number) => void;
  logout: (redirect?: boolean) => Promise<void>;
  resetLocalSession: () => void;
  hydrate: () => void;
  setUser: (user: User) => void;
  updateSessionExpiry: (expiresIn: number) => void;
}

export const useAuthStore = create<AuthState>(set => ({
  user: null,
  expiresAt: null,
  isAuthenticated: false,

  login: (user, expiresIn) => {
    const normalizedUser = normalizeUser(user);
    const expiresAt = Date.now() + expiresIn * 1000;
    clearClientAuthStorage();
    localStorage.setItem('expiresAt', expiresAt.toString());
    localStorage.setItem('user', JSON.stringify(normalizedUser));
    set({ user: normalizedUser, expiresAt, isAuthenticated: true });
  },

  logout: async (redirect = true) => {
    const logoutUrl = await notifyBackendLogout();

    clearClientAuthStorage();
    setStoredActiveTenantId(null);

    if (redirect) {
      window.location.replace(logoutUrl || buildKeycloakLogoutUrl());
      return;
    }

    set({
      user: null,
      expiresAt: null,
      isAuthenticated: false,
    });
  },

  resetLocalSession: () => {
    clearClientAuthStorage();
    setStoredActiveTenantId(null);
    set({
      user: null,
      expiresAt: null,
      isAuthenticated: false,
    });
  },

  hydrate: () => {
    clearLegacyTokenStorage();
    const expiresAtStr = localStorage.getItem('expiresAt');
    const userJson = localStorage.getItem('user');

    if (userJson) {
      try {
        const user = normalizeUser(JSON.parse(userJson) as User);
        const expiresAt = expiresAtStr ? parseInt(expiresAtStr, 10) : null;
        const now = Date.now();

        if (expiresAt !== null && Number.isFinite(expiresAt) && expiresAt <= now) {
          clearClientAuthStorage();
          set({
            user: null,
            expiresAt: null,
            isAuthenticated: false,
          });
          return;
        }

        set({
          user,
          expiresAt,
          isAuthenticated: true,
        });
        localStorage.setItem('user', JSON.stringify(user));
      } catch {
        // Corrupted data — clear everything
        clearClientAuthStorage();
        setStoredActiveTenantId(null);
        set({
          user: null,
          expiresAt: null,
          isAuthenticated: false,
        });
      }
    }
  },

  setUser: user => {
    const normalizedUser = normalizeUser(user);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
    set({ user: normalizedUser });
  },

  updateSessionExpiry: expiresIn => {
    const expiresAt = Date.now() + expiresIn * 1000;
    localStorage.setItem('expiresAt', expiresAt.toString());
    set({ expiresAt });
  },
}));
